import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Tag, Drawer, Descriptions, Spin, message, Card, Select, Form, Alert, Modal, DatePicker } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';
import reqwest from 'reqwest';
import config from '../../config';
import { getToken } from '../../Utils/common';
import MedicalSchemaMedications from '../MedicalSchemaMedications';
import MedicalSchemaHistory from '../MedicalSchemaHistory';

moment.locale('es');

const HospitalizationMedicalSchemas = ({ hospitalizationId, hospitalizationStatus, activeMedicalSchemaId, onChanged, isReadOnly }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [creating, setCreating] = useState(false);
  const [schemaRefresh, setSchemaRefresh] = useState(0);
  const [schemaDetail, setSchemaDetail] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaSaving, setSchemaSaving] = useState(false);
  // Token to auto-open the medication add drawer after creating a schema
  const [autoOpenAddToken, setAutoOpenAddToken] = useState(null);

  // Create schema drawer state
  const [createVisible, setCreateVisible] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createForm] = Form.useForm();
  const [previousSchemas, setPreviousSchemas] = useState([]);
  const [previousSchemasLoading, setPreviousSchemasLoading] = useState(false);

  const isHospitalizationActive = hospitalizationStatus === 'A' && !isReadOnly;

  const fetchItems = () => {
    if (!hospitalizationId) return;
    setLoading(true);
    reqwest({
      url: `${config.api_url}/hospitalizationmedicalschema/?hospitalization=${hospitalizationId}`,
      method: 'get',
      type: 'json',
      headers: { 'Authorization': `Token ${getToken()}` },
    }).then((resp) => {
      const results = resp.results || resp; // support both paginated/non-paginated
      setItems(results);
    }).catch(() => message.error('No se pudieron obtener los esquemas médicos')).always(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalizationId]);

  const openDetail = (record) => {
    setSelected(record);
    setDetailVisible(true);
    setSchemaRefresh((v) => v + 1);
    loadSchemaDetail(record.medical_schema);
  };

  const loadSchemaDetail = async (schemaId) => {
    if (!schemaId) return;
    setSchemaLoading(true);
    try {
      const resp = await reqwest({
        url: `${config.api_url}/medicalschema/${schemaId}/`,
        method: 'get',
        type: 'json',
        headers: { 'Authorization': `Token ${getToken()}` },
      });
      setSchemaDetail(resp);
    } catch (e) {
      message.error('No se pudo cargar el detalle del esquema');
    } finally {
      setSchemaLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailVisible(false);
    setSelected(null);
    setSchemaDetail(null);
    setSelectedRowKeys([]);
  };

  const handlePrintSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    
    // selectedRowKeys contains IDs of HospitalizationMedicalSchema
    // but the backend print_selected expects MedicalSchema IDs
    const selectedSchemas = items
      .filter(item => selectedRowKeys.includes(item.id))
      .map(item => item.medical_schema);

    try {
      setIsPrinting(true);
      const resp = await window.fetch(`${config.api_url}/medicalschema/print_selected/`, {
        method: 'POST',
        headers: { 
          'Authorization': `Token ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: selectedSchemas })
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Error HTTP ${resp.status}`);
      }

      const blob = await resp.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = 'esquemas_seleccionados.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlBlob);
    } catch (err) {
      message.error(err.message || 'No se pudo generar el PDF');
    } finally {
      setIsPrinting(false);
    }
  };

  const createNewSchema = () => {
    if (!hospitalizationId) return;
    createForm.resetFields();
    setCreateVisible(true);
  };

  const onCreateSave = async () => {
    if (!hospitalizationId) return;
    try {
      const values = await createForm.validateFields();
      setCreateSaving(true);
      // 1) Create MedicalSchema with selected type
      const payload = { 
        schema_type: values.schema_type || null,
        copy_from: values.copy_from || null
      };
      if (values.schema_type === 'REF') {
        payload.applied_date = values.applied_date ? values.applied_date.format('YYYY-MM-DD') : null;
      }
      const schema = await reqwest({
        url: `${config.api_url}/medicalschema/`,
        method: 'post',
        contentType: 'application/json',
        data: JSON.stringify(payload),
        headers: { 'Authorization': `Token ${getToken()}` },
        type: 'json'
      });
      // 2) Link to hospitalization as active via through model
      const link = await reqwest({
        url: `${config.api_url}/hospitalizationmedicalschema/`,
        method: 'post',
        contentType: 'application/json',
        data: JSON.stringify({ hospitalization: hospitalizationId, medical_schema: schema.id, active: true }),
        headers: { 'Authorization': `Token ${getToken()}` },
        type: 'json'
      });

      setCreateVisible(false);
      message.success('Esquema médico creado y activado');
      // Refresh list and open detail of the new schema to start loading medications
      await fetchItems();
      // Open detail with the returned link if possible
      const linkRecord = link && link.id ? link : (items.find(i => i.medical_schema === schema.id) || null);
      if (linkRecord) {
        openDetail(linkRecord);
        // Trigger auto-open of medication add drawer in child component
        setTimeout(() => setAutoOpenAddToken(Date.now()), 0);
      }
    } catch (e) {
      if (e && e.errorFields) {
        // validation error from form
        return;
      }
      message.error('No se pudo crear el nuevo esquema médico');
    } finally {
      setCreateSaving(false);
    }
  };

  const columns = [
    {
      title: 'ID Esquema',
      dataIndex: ['medical_schema'],
      key: 'medical_schema',
      render: (val) => `#${val}`,
    },
    {
      title: 'Tipo',
      dataIndex: 'medical_schema_type_display',
      key: 'medical_schema_type_display',
      render: (val, record) => {
        if (!val) return '-';
        let color = 'blue';
        const code = record && record.medical_schema_type;
        if (code === 'PSI' || val === 'Psiquiátrico') color = 'purple';
        else if (code === 'CLI' || val === 'Clínico') color = 'blue';
        else if (code === 'REF' || val === 'Refuerzo') color = 'volcano';
        return <Tag color={color}>{val}</Tag>;
      }
    },
    {
      title: 'Creado por',
      dataIndex: 'medical_schema_created_by_username',
      key: 'medical_schema_created_by_username',
      render: (val) => val || '-',
    },
    {
      title: 'Actualizado por',
      dataIndex: 'medical_schema_updated_by_username',
      key: 'medical_schema_updated_by_username',
      render: (val) => val || '-',
    },
    {
      title: 'Creado (vínculo)',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val) => val ? moment(val).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Actualizado (vínculo)',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (val) => val ? moment(val).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Activo',
      dataIndex: 'active',
      key: 'active',
      render: (val) => val ? <Tag color="green">Activo</Tag> : <Tag>No activo</Tag>,
    },
  ];

  return (
    <Card
      title="Esquemas Médicos"
      extra={
        <div>
          <Button type="default" style={{ marginRight: 8 }} onClick={fetchItems} loading={loading}>Refrescar</Button>
          <Button 
            type="default" 
            style={{ marginRight: 8 }} 
            onClick={handlePrintSelected} 
            loading={isPrinting} 
            disabled={selectedRowKeys.length === 0}
            icon={<PrinterOutlined />}
          >
            Imprimir ({selectedRowKeys.length})
          </Button>
          <Button type="primary" onClick={createNewSchema} disabled={!isHospitalizationActive}>Nuevo esquema</Button>
        </div>
      }
    >
      <Table
        size="small"
        rowKey={(r) => r.id}
        columns={columns}
        dataSource={items}
        loading={{ spinning: loading, tip: 'Cargando...' }}
        pagination={false}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        onRow={(record) => ({ 
          onClick: (e) => {
            // Avoid opening detail if clicking on checkbox or its container
            if (e.target.closest('.ant-table-selection-column')) return;
            openDetail(record);
          }
        })}
      />

      {/* Create schema drawer */}
      <Drawer
        title="Nuevo esquema médico"
        visible={createVisible}
        open={createVisible}
        width={480}
        onClose={() => setCreateVisible(false)}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setCreateVisible(false)} style={{ marginRight: 8 }}>Cancelar</Button>
            <Button type="primary" loading={createSaving} onClick={onCreateSave}>Guardar</Button>
          </div>
        }
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="schema_type" label="Tipo de esquema" rules={[{ required: true, message: 'Seleccione el tipo de esquema' }]}>
            <Select placeholder="Seleccione tipo" onChange={() => {
              createForm.setFieldsValue({ copy_from: undefined });
              setPreviousSchemas([]);
            }}>
              <Select.Option value="PSI">Psiquiátrico</Select.Option>
              <Select.Option value="CLI">Clínico</Select.Option>
              <Select.Option value="REF">Refuerzo</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item shouldUpdate={(prevValues, curValues) => prevValues.schema_type !== curValues.schema_type} noStyle>
            {({ getFieldValue }) => {
              const sel = getFieldValue('schema_type');
              if (sel) {
                return (
                  <Form.Item name="copy_from" label="Basado en (opcional)" tooltip="Se copiarán los medicamentos del esquema seleccionado">
                    <Select
                      placeholder="Seleccione un esquema anterior para copiar"
                      loading={previousSchemasLoading}
                      allowClear
                      onDropdownVisibleChange={(open) => {
                        if (open && previousSchemas.length === 0) {
                          setPreviousSchemasLoading(true);
                          reqwest({
                            url: `${config.api_url}/hospitalizationmedicalschema/?hospitalization=${hospitalizationId}&medical_schema__schema_type=${sel}&ordering=-created_at&page_size=10`,
                            method: 'get',
                            type: 'json',
                            headers: { 'Authorization': `Token ${getToken()}` },
                          }).then((resp) => {
                            const results = resp.results || resp;
                            setPreviousSchemas(results);
                          }).catch(() => {
                            // error handling
                          }).always(() => setPreviousSchemasLoading(false));
                        }
                      }}
                    >
                      {previousSchemas.map(ps => (
                        <Select.Option key={ps.medical_schema} value={ps.medical_schema}>
                          #{ps.medical_schema} - {moment(ps.created_at).format('DD/MM/YYYY HH:mm')} {ps.active ? '(Activo)' : ''}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const sel = getFieldValue('schema_type');
              const sameTypeActives = sel ? (items || []).filter(it => it.active && it.medical_schema_type === sel) : [];
              if (!sel) {
                return (
                  <Alert
                    type="info"
                    showIcon
                    message="Activación de esquemas por tipo"
                    description="Al activar un esquema, si existe otro activo del mismo tipo, será desactivado automáticamente."
                  />
                );
              }
              // Campo adicional para tipo Refuerzo: Fecha de Aplicación
              const extraFields = sel === 'REF' ? (
                <Form.Item
                  name="applied_date"
                  label="Fecha de Aplicación"
                  rules={[{ required: true, message: 'Seleccione la fecha de aplicación' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => current && current.startOf('day') < moment().startOf('day')}
                  />
                </Form.Item>
              ) : null;

              if (sameTypeActives.length > 0) {
                const ids = sameTypeActives.map(it => `#${it.medical_schema}`).join(', ');
                return (
                  <div>
                    {extraFields}
                    <Alert
                      type="warning"
                      showIcon
                      message="Se desactivarán otros esquemas del mismo tipo"
                      description={`Actualmente tiene activo(s) del mismo tipo: ${ids}. Al guardar, estos se desactivarán.`}
                    />
                  </div>
                );
              }
              return (
                <div>
                  {extraFields}
                  <Alert
                    type="success"
                    showIcon
                    message="No hay esquemas activos de este tipo"
                    description="Puede crear y activar el esquema sin afectar otros tipos."
                  />
                </div>
              );
            }}
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title={selected ? `Esquema médico #${selected.medical_schema}` : 'Esquema médico'}
        visible={detailVisible}
        open={detailVisible}
        width={"50%"}
        onClose={closeDetail}
        destroyOnClose
      >
        {selected ? (
          <>
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 12 }}>
              <Descriptions.Item label="ID Esquema">{selected.medical_schema}</Descriptions.Item>
              <Descriptions.Item label="Creado por">{schemaDetail && (schemaDetail.created_by_username || (schemaDetail.created_by ? `#${schemaDetail.created_by}` : '-'))}</Descriptions.Item>
              <Descriptions.Item label="Actualizado por">{schemaDetail && (schemaDetail.updated_by_username || (schemaDetail.updated_by ? `#${schemaDetail.updated_by}` : '-'))}</Descriptions.Item>
              <Descriptions.Item label="Vínculo creado">{selected.created_at ? moment(selected.created_at).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="Vínculo actualizado">{selected.updated_at ? moment(selected.updated_at).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="Estado">
                {selected.active ? <Tag color="green">Activo</Tag> : <Tag>No activo</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de esquema">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Select
                    style={{ minWidth: 220 }}
                    placeholder="Seleccione tipo"
                    loading={schemaLoading || schemaSaving}
                    value={schemaDetail ? (schemaDetail.schema_type || undefined) : undefined}
                    onChange={async (val) => {
                      if (!selected || !isHospitalizationActive) return;
                      const currentLinkId = selected.id;
                      const targetType = val;
                      // Buscar si existe otro esquema ACTIVO del mismo tipo en esta internación
                      const conflict = (items || []).find(it => it.active && it.medical_schema_type === targetType && it.id !== currentLinkId);

                      const doPatchType = async () => {
                        try {
                          setSchemaSaving(true);
                          await reqwest({
                            url: `${config.api_url}/medicalschema/${selected.medical_schema}/`,
                            method: 'patch',
                            contentType: 'application/json',
                            data: JSON.stringify({ schema_type: targetType }),
                            headers: { 'Authorization': `Token ${getToken()}` },
                            type: 'json'
                          });
                          message.success('Tipo de esquema actualizado');
                          // Reload detail and refresh list to reflect changes in the table
                          loadSchemaDetail(selected.medical_schema);
                          fetchItems();
                        } catch (e) {
                          // Mostrar mensaje de error específico si viene del backend
                          const msg = (e && e.response && e.response.detail) || 'No se pudo actualizar el tipo de esquema';
                          message.error(msg);
                        } finally {
                          setSchemaSaving(false);
                        }
                      };

                      if (conflict) {
                        Modal.confirm({
                          title: 'Ya existe un esquema activo de este tipo',
                          content: `La internación ya tiene un esquema activo de este tipo (#${conflict.medical_schema}). Para que este quede activo, se desactivará el anterior. ¿Desea continuar?`,
                          okText: 'Desactivar y actualizar',
                          cancelText: 'Cancelar',
                          async onOk() {
                            try {
                              setSchemaSaving(true);
                              // 1) Desactivar el vínculo en conflicto
                              await reqwest({
                                url: `${config.api_url}/hospitalizationmedicalschema/${conflict.id}/`,
                                method: 'patch',
                                contentType: 'application/json',
                                data: JSON.stringify({ active: false }),
                                headers: { 'Authorization': `Token ${getToken()}` },
                                type: 'json'
                              });
                              // 2) Actualizar el tipo del esquema actual
                              await doPatchType();
                            } catch (e) {
                              message.error('No se pudo desactivar el esquema en conflicto');
                            } finally {
                              setSchemaSaving(false);
                            }
                          },
                        });
                        return;
                      }

                      // Si no hay conflicto, aplicar directamente
                      await doPatchType();
                    }}
                    disabled={!selected.active || !isHospitalizationActive}
                    allowClear
                  >
                    <Select.Option value="PSI">Psiquiátrico</Select.Option>
                    <Select.Option value="CLI">Clínico</Select.Option>
                    <Select.Option value="REF">Refuerzo</Select.Option>
                  </Select>
                  {/* Campo de Fecha de Aplicación para tipo Refuerzo (editable) */}
                  {schemaDetail && schemaDetail.schema_type === 'REF' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#666' }}>Fecha de Aplicación:</span>
                      <DatePicker
                        value={schemaDetail.applied_date ? moment(schemaDetail.applied_date) : null}
                        format="DD/MM/YYYY"
                        allowClear={false}
                        disabledDate={(current) => current && current.startOf('day') < moment().startOf('day')}
                        disabled={!isHospitalizationActive}
                        onChange={async (date) => {
                          if (!date) return;
                          try {
                            setSchemaSaving(true);
                            await reqwest({
                              url: `${config.api_url}/medicalschema/${selected.medical_schema}/`,
                              method: 'patch',
                              contentType: 'application/json',
                              data: JSON.stringify({ applied_date: date.format('YYYY-MM-DD') }),
                              headers: { 'Authorization': `Token ${getToken()}` },
                              type: 'json'
                            });
                            message.success('Fecha de Aplicación actualizada');
                            loadSchemaDetail(selected.medical_schema);
                            fetchItems();
                          } catch (e) {
                            message.error('No se pudo actualizar la Fecha de Aplicación');
                          } finally {
                            setSchemaSaving(false);
                          }
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </Descriptions.Item>
              {schemaDetail && schemaDetail.schema_type === 'REF' ? (
                <Descriptions.Item label="Aplicación (Refuerzo)">
                  {schemaDetail.applied ? <Tag color="green">Aplicado</Tag> : <Tag color="red">No aplicado</Tag>}
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            {/* Details of schema: medications and history */}
            <Spin spinning={schemaLoading}>
              <MedicalSchemaMedications 
                key={`meds-${selected.medical_schema}-${schemaRefresh}`} 
                medicalSchemaId={selected.medical_schema} 
                schemaType={schemaDetail ? schemaDetail.schema_type : null}
                readOnly={!selected.active || !isHospitalizationActive} 
                onChanged={() => setSchemaRefresh((v) => v + 1)} 
                autoOpenToken={autoOpenAddToken} 
              />
              <div style={{ marginTop: 16 }}>
                <MedicalSchemaHistory key={`hist-${selected.medical_schema}-${schemaRefresh}`} medicalSchemaId={selected.medical_schema} />
              </div>
            </Spin>
          </>
        ) : null}
      </Drawer>
    </Card>
  );
};

export default HospitalizationMedicalSchemas;
